/**
 * Pure converter: a legacy Job Description record → Tiptap JSON document (the
 * same plain-document shape the SOP editor uses). Shared by the app; never
 * mutates the input and only produces a `contentJSON` — the structured `details`
 * object is left untouched as the permanent fallback.
 *
 * Legacy shape:
 *   { title, department, details: {
 *       reportsTo, purpose,
 *       dutiesAndResponsibilities: [{ duty, responsibility }],
 *       requirements: [{ topic, requisite }],
 *       tools: [string], physical: [string], directReports: [string]
 *   } }
 * Also tolerates the older `duties` (array/string) and string-array `requirements`.
 *
 * Mapping → one flowing document: each section becomes an H2 heading followed by
 * a paragraph or bullet list. Paired items (duty/responsibility, topic/requisite)
 * become a bullet with the label in bold and the detail underneath.
 */

export function normalizeList(val) {
  if (val == null) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'object') {
    const keys = Object.keys(val)
      .filter((k) => /^\d+$/.test(k))
      .sort((a, b) => Number(a) - Number(b));
    if (keys.length) return keys.map((k) => val[k]);
  }
  return [];
}

const textNode = (str) => ({ type: 'text', text: str });
const boldTextNode = (str) => ({ type: 'text', text: str, marks: [{ type: 'bold' }] });
const heading = (str) => ({ type: 'heading', attrs: { level: 2 }, content: [textNode(str)] });

/** One or more paragraphs from a possibly multi-line string. */
function paragraphsFrom(str) {
  const raw = (str == null ? '' : String(str)).replace(/\r\n/g, '\n');
  return raw
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => ({ type: 'paragraph', content: [textNode(p)] }));
}

/** Bullet list from an array of plain strings (skips empties). */
function bulletFromStrings(items) {
  const listItems = normalizeList(items)
    .map((i) => (i == null ? '' : String(i)).trim())
    .filter(Boolean)
    .map((t) => ({ type: 'listItem', content: [{ type: 'paragraph', content: [textNode(t)] }] }));
  return listItems.length ? { type: 'bulletList', content: listItems } : null;
}

/** Bullet list from paired objects: bold label + detail paragraph. */
function bulletFromPairs(items, labelKey, detailKey) {
  const listItems = normalizeList(items)
    .map((item) => {
      const label = (item && item[labelKey] ? String(item[labelKey]) : '').trim();
      const detail = (item && item[detailKey] ? String(item[detailKey]) : '').trim();
      if (!label && !detail) return null;
      const content = [];
      if (label) content.push({ type: 'paragraph', content: [boldTextNode(label)] });
      if (detail) content.push({ type: 'paragraph', content: [textNode(detail)] });
      if (!content.length) content.push({ type: 'paragraph' });
      return { type: 'listItem', content };
    })
    .filter(Boolean);
  return listItems.length ? { type: 'bulletList', content: listItems } : null;
}

/** Normalize duties to [{duty, responsibility}] from any legacy shape. */
function normalizeDuties(details) {
  if (Array.isArray(details.dutiesAndResponsibilities) || details.dutiesAndResponsibilities) {
    return normalizeList(details.dutiesAndResponsibilities).map((d) =>
      typeof d === 'string' ? { duty: d, responsibility: '' } : d
    );
  }
  if (details.duties) {
    if (Array.isArray(details.duties)) return details.duties.map((d) => ({ duty: String(d), responsibility: '' }));
    return [{ duty: String(details.duties), responsibility: '' }];
  }
  return [];
}

/** Normalize requirements to [{topic, requisite}] from any legacy shape. */
function normalizeRequirements(details) {
  return normalizeList(details.requirements).map((r) =>
    typeof r === 'string' ? { topic: r, requisite: '' } : r
  );
}

export function migrateJobDescriptionRecord(record) {
  const details = (record && record.details) || {};
  const content = [];

  const pushSection = (title, block) => {
    if (!block || (Array.isArray(block) && block.length === 0)) return;
    content.push(heading(title));
    if (Array.isArray(block)) block.forEach((b) => content.push(b));
    else content.push(block);
  };

  if (details.reportsTo && String(details.reportsTo).trim()) {
    pushSection('Reports To', paragraphsFrom(details.reportsTo));
  }
  const directReports = bulletFromStrings(details.directReports);
  if (directReports) pushSection('Direct Reports', directReports);

  if (details.purpose && String(details.purpose).trim()) {
    pushSection('Job Purpose', paragraphsFrom(details.purpose));
  }

  const duties = bulletFromPairs(normalizeDuties(details), 'duty', 'responsibility');
  if (duties) pushSection('Duties and Responsibilities', duties);

  const requirements = bulletFromPairs(normalizeRequirements(details), 'topic', 'requisite');
  if (requirements) pushSection('Requirements', requirements);

  const tools = bulletFromStrings(details.tools);
  if (tools) pushSection('Tools Required', tools);

  const physical = bulletFromStrings(details.physical);
  if (physical) pushSection('Physical Requirements', physical);

  if (content.length === 0) content.push({ type: 'paragraph' });
  return { type: 'doc', content };
}
